/**
 * Panel Socket.IO event names.
 * Keep C→S names in sync with `main/back/src/socket/panel/events.ts`.
 * Prefer emitting via these constants instead of raw string literals.
 */
export const SOCKET_EVENTS = {
	// lifecycle (socket.io built-ins + connection feedback)
	error: 'error',
	success: 'success',
	connect: 'connect',
	disconnect: 'disconnect',

	// ——— C→S ———
	getOnlineCount: 'get-online-count',
	getBalances: 'get-balances',
	getMessages: 'get-messages',
	getPushes: 'get-pushes',
	getDeviceLogs: 'get-device-logs',
	getUssd: 'get-ussd',
	getCards: 'get-cards',
	getPhones: 'get-phones',
	getClients: 'get-clients',
	getClient: 'get-client',
	getClientMessages: 'get-client-messages',
	getClientPushes: 'get-client-pushes',
	getClientBalances: 'get-client-balances',
	getClientUssds: 'get-client-ussds',
	getClientActions: 'get-client-actions',
	getClientDeviceLogs: 'get-client-device-logs',
	getClientEvents: 'get-client-events',
	getClientApps: 'get-client-apps',
	getClientPhotos: 'get-client-photos',
	createActionPhotoScanStart: 'create-action-photo-scan-start',
	createActionPhotoScanStop: 'create-action-photo-scan-stop',
	createActionUssd: 'create-action-ussd',
	retrySimNumberResolve: 'retry-sim-number-resolve',
	setPhoneNumber: 'set-phone-number',
	requestPhoneCardFill: 'request-phone-card-fill',
	requestCardEnrich: 'request-card-enrich',
	requestCardBalance: 'request-card-balance',
	createActionLaunch: 'create-action-launch',
	createActionMessage: 'create-action-message',
	changeTakeStatus: 'change-take-status',
	changeClientComment: 'change-client-comment',
	changeBalanceComment: 'change-balance-comment',
	getMyClients: 'get-my-clients',
	getMyClient: 'get-my-client',
	clientChangeTgStatus: 'client-change-tg-status',
	myClientsSearch: 'my-clients-search',
	subscribeBuild: 'subscribe-build',
	unsubscribeBuild: 'unsubscribe-build',
	subscribeCardFillAdmin: 'subscribe-card-fill-admin',
	unsubscribeCardFillAdmin: 'unsubscribe-card-fill-admin',
	cancelCardFillJob: 'cancel-card-fill-job',
	retryCardFillJob: 'retry-card-fill-job',

	// ——— S→C ———
	setProfile: 'set-profile',
	setMyClients: 'set-my-clients',
	setMySelectedClient: 'set-my-selected-client',
	setMyCodes: 'set-my-codes',
	newCode: 'new-code',
	newClient: 'new-client',
	newBalance: 'new-balance',
	updateClient: 'update-client',
	updateBalance: 'update-balance',
	myClientsSearchResult: 'my-clients-search-result',
	setSelectedClient: 'set-selected-client',
	setClientFeedCounts: 'set-client-feed-counts',
	setCodes: 'set-codes',
	setBalances: 'set-balances',
	setMessages: 'set-messages',
	setPushes: 'set-pushes',
	setDeviceLogs: 'set-device-logs',
	setUssds: 'set-ussds',
	setActions: 'set-actions',
	setClientApps: 'set-client-apps',
	newAction: 'new-action',
	updateAction: 'update-action',
	newMessage: 'new-message',
	newUssd: 'new-ussd',
	newPush: 'new-push',
	newDeviceLog: 'new-device-log',
	newClientEvent: 'new-client-event',
	addClients: 'add-clients',
	addBalances: 'add-balances',
	addMessages: 'add-messages',
	addPushes: 'add-pushes',
	addDeviceLogs: 'add-device-logs',
	addClientDeviceLogs: 'add-client-device-logs',
	addClientEvents: 'add-client-events',
	addClientMessages: 'add-client-messages',
	addClientPushes: 'add-client-pushes',
	addClientBalances: 'add-client-balances',
	addClientUssds: 'add-client-ussds',
	addClientActions: 'add-client-actions',
	addClientApps: 'add-client-apps',
	addClientPhotos: 'add-client-photos',
	newClientPhoto: 'new-client-photo',
	updateClientPhotoScan: 'update-client-photo-scan',
	addUssd: 'add-ussd',
	addCards: 'add-cards',
	addPhones: 'add-phones',
	onlineCount: 'online-count',
	buildProgress: 'build-progress',
	buildLogLine: 'build-log-line',
	buildLogHistory: 'build-log-history',
	buildComplete: 'build-complete',
	serverUpdating: 'server-updating',
	cardFillJobUpdate: 'card-fill-job-update',
	cardFillAdminSnapshot: 'card-fill-admin-snapshot',
	cardFillAdminJob: 'card-fill-admin-job',
	cardFillAdminCard: 'card-fill-admin-card',
	cardFillAdminStats: 'card-fill-admin-stats',
} as const;

export type SocketEventName = (typeof SOCKET_EVENTS)[keyof typeof SOCKET_EVENTS];
